import React, { useState, useEffect, useCallback } from 'react';
import './Products.css';
import './AllSellStyles.css';
import './CommonStyles.css';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';
import { getPurchasePaymentReport, getPurchasePaymentFilterOptions } from '../../services/reportApi';
import { formatDateTime } from '../../utils/dateUtils';
import ViewPaymentsModal from './ViewPaymentsModal';

const PurchasePaymentReport = () => {
    const [entriesPerPage, setEntriesPerPage] = useState(25);
    const [searchTerm, setSearchTerm] = useState('');
    const [showExportDropdown, setShowExportDropdown] = useState(false);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [lastPage, setLastPage] = useState(1);
    const [totalAmountFormatted, setTotalAmountFormatted] = useState('$ 0.00');
    const [filterOptions, setFilterOptions] = useState({ locations: [], suppliers: [] });
    const [filters, setFilters] = useState({ start_date: '', end_date: '', location_id: '', supplier_id: '' });
    const [pendingFilters, setPendingFilters] = useState({ ...filters });
    const [showFilters, setShowFilters] = useState(false);

    const [selectedRowForView, setSelectedRowForView] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);

    const fetchData = useCallback(async (pg = 1, perPage = entriesPerPage) => {
        setLoading(true); setError(null);
        try {
            const res = await getPurchasePaymentReport({ ...filters, page: pg, per_page: perPage });
            if (res.success) {
                setData(res.data.data || []);
                setTotal(res.data.total || 0);
                setLastPage(res.data.last_page || 1);
                setPage(res.data.current_page || pg);
                if (res.data.total_amount_formatted) {
                    setTotalAmountFormatted(res.data.total_amount_formatted);
                }
            }
        } catch (e) { setError(e.message); } finally { setLoading(false); }
    }, [filters, entriesPerPage]);

    useEffect(() => {
        fetchData(1, entriesPerPage);
        getPurchasePaymentFilterOptions().then(r => { if (r.success) setFilterOptions(r.data); }).catch(() => {});
    }, []);
    useEffect(() => { fetchData(1, entriesPerPage); }, [entriesPerPage]);

    const handleOpenFilters = () => {
        setPendingFilters({ ...filters });
        setShowFilters(true);
    };

    const handleFilterApply = () => {
        setFilters({ ...pendingFilters });
        setShowFilters(false);
    };

    const handleFilterReset = () => setPendingFilters({ start_date: '', end_date: '', location_id: '', supplier_id: '' });

    const handleViewPayment = (row) => {
        setSelectedRowForView(row);
        setShowViewModal(true);
    };

    const filteredData = data.filter(r =>
        (r.payment_ref_no || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.supplier || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.ref_no || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    const buildMethodLabel = (row) => {
        let m = row.method_label || row.method || '';
        if (row.method === 'cheque' && row.cheque_number) m += `\n(Cheque No.: ${row.cheque_number})`;
        else if (row.method === 'card' && row.card_transaction_number) m += `\n(Card No.: ${row.card_transaction_number})`;
        else if (row.method === 'bank_transfer' && row.bank_account_number) m += `\n(Bank A/C: ${row.bank_account_number})`;
        return m;
    };

    const tableHeaders = ['REFERENCE NO', 'PAID ON', 'AMOUNT', 'SUPPLIER', 'PAYMENT METHOD', 'PURCHASE', 'ACTION'];

    const handleExport = (format) => {
        const headers = ['Reference No', 'Paid on', 'Amount', 'Supplier', 'Payment Method', 'Purchase'];
        const rows = filteredData.map(r => [
            r.payment_ref_no || '', r.paid_on ? formatDateTime(r.paid_on) : '',
            r.amount_formatted || '', r.supplier || '',
            buildMethodLabel(r).replace(/\n/g, ' '), r.ref_no || ''
        ]);
        const title = 'Purchase Payment Report'; const filename = 'purchase_payment_report';
        if (format === 'CSV') exportToCSV(headers, rows, filename);
        else if (format === 'Excel') exportToExcel(headers, rows, filename);
        else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
        else if (format === 'Print') printPage();
        setShowExportDropdown(false);
    };

    const from = total === 0 ? 0 : (page - 1) * entriesPerPage + 1;
    const to = Math.min(page * entriesPerPage, total);

    return (
        <div style={{ paddingTop: '0.5rem' }}>

            {/* ── Table Container ── */}
            <div style={{ background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>

                {/* Table controls */}
                <div className="table-controls" style={{ borderBottom: '1px solid var(--etpos-border, #e2e8f0)', padding: '1rem 1.25rem', position: 'relative', zIndex: 20 }}>
                    <div className="table-controls-left">
                        <div className="entries-select">
                            <span>Show</span>
                            <select value={entriesPerPage} onChange={e => setEntriesPerPage(Number(e.target.value))}>
                                <option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
                            </select>
                            <span>entries</span>
                        </div>
                    </div>
                    <div className="table-controls-right" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {/* Filters button placed right before Export button */}
                        <button
                            onClick={handleOpenFilters}
                            style={{
                                padding: '0.4rem 0.8rem',
                                border: '1px solid var(--etpos-border, #e2e8f0)',
                                borderRadius: '6px',
                                background: 'var(--etpos-card, #fff)',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                color: 'var(--etpos-text, #334155)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.4rem',
                                fontWeight: '500'
                            }}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                            </svg>
                            Filters
                        </button>

                        <div className="export-dropdown-container" style={{ position: 'relative', zIndex: 100 }}>
                            <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.8rem', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '6px', background: 'var(--etpos-card, #fff)', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--etpos-text, #334155)' }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                Export ▾
                            </button>
                            {showExportDropdown && (
                                <div className="export-dropdown-menu" style={{ position: 'absolute', top: '100%', right: 0, marginTop: '0.25rem', background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '6px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)', zIndex: 9999, minWidth: '160px' }}>
                                    {[
                                        { label: 'Export CSV', action: () => handleExport('CSV'), icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg> },
                                        { label: 'Export Excel', action: () => handleExport('Excel'), icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M8 13h8"/><path d="M8 17h8"/></svg> },
                                        { label: 'Print', action: () => handleExport('Print'), icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg> },
                                        { label: 'Export PDF', action: () => handleExport('PDF'), icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M9 15v-6h2a2 2 0 0 1 0 4H9"/></svg> }
                                    ].map((item, i) => (
                                        <button key={i} onClick={() => { if (item.action) item.action(); setShowExportDropdown(false); }} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', width: '100%', padding: '0.55rem 1rem', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--etpos-bg, #f1f5f9)'} onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                                            {item.icon}
                                            {item.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <input type="text" placeholder="Search ..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ padding: '0.4rem 0.75rem', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '6px', fontSize: '0.8125rem', outline: 'none', width: '180px', background: 'var(--etpos-card, #fff)', color: 'var(--etpos-text, #334155)' }} />
                    </div>
                </div>

                {error && <div style={{ padding: '1rem', color: '#ef4444', fontSize: '0.875rem' }}>Error: {error}</div>}

                {/* Table */}
                <div style={{ overflowX: 'auto' }}>
                    <table className="products-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                        <thead>
                            <tr style={{ background: 'var(--etpos-bg, #f8fafc)', borderBottom: '1px solid var(--etpos-border, #e2e8f0)' }}>
                                {tableHeaders.map(h => (
                                    <th key={h} style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '600', whiteSpace: 'nowrap' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="7" style={{ padding: '2rem', textAlign: 'center', color: 'var(--etpos-text-muted, #64748b)' }}>Loading...</td></tr>
                            ) : filteredData.length === 0 ? (
                                <tr><td colSpan="7" style={{ padding: '2rem', textAlign: 'center', color: 'var(--etpos-text-muted, #64748b)' }}>No data found</td></tr>
                            ) : filteredData.map((row, idx) => (
                                <tr key={idx} style={{ borderBottom: '1px solid var(--etpos-border, #f1f5f9)' }}>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)' }}>{row.payment_ref_no}</td>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)', whiteSpace: 'nowrap' }}>{row.paid_on ? formatDateTime(row.paid_on) : ''}</td>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#10b981', fontWeight: '600' }}>{row.amount_formatted}</td>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)', whiteSpace: 'pre-line' }}>{row.supplier}</td>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)', whiteSpace: 'pre-line' }}>{buildMethodLabel(row)}</td>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#2563eb', fontWeight: '600', cursor: 'pointer' }}>{row.ref_no}</td>
                                    <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                                        <button onClick={() => handleViewPayment(row)} style={{ padding: '0.25rem 0.75rem', background: 'var(--etpos-card, #fff)', border: '1px solid #a855f7', color: '#a855f7', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer' }}>View</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr style={{ background: 'var(--etpos-bg, #f1f5f9)', borderTop: '2px solid var(--etpos-border, #cbd5e1)', fontWeight: '700' }}>
                                <td colSpan="2" style={{ textAlign: 'right', padding: '0.75rem 1rem', fontSize: '0.875rem', color: 'var(--etpos-text, #1e293b)' }}>
                                    Total:
                                </td>
                                <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '700', whiteSpace: 'nowrap' }}>
                                    {totalAmountFormatted}
                                </td>
                                <td colSpan="4"></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                {/* Pagination */}
                <div className="pagination" style={{ padding: '0.85rem 1.25rem', borderTop: '1px solid var(--etpos-border, #e2e8f0)' }}>
                    <div className="pagination-info" style={{ fontSize: '0.8125rem', color: 'var(--etpos-text-muted, #475569)' }}>
                        {total > 0 ? `Showing ${from} to ${to} of ${total} entries` : 'No entries'}
                    </div>
                    <div className="pagination-controls" style={{ display: 'flex', gap: '0.5rem' }}>
                        <button className="pagination-btn" disabled={page <= 1} onClick={() => { if (page > 1) fetchData(page - 1); }}>Previous</button>
                        <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem', fontSize: '0.875rem', color: 'var(--etpos-text-muted, #475569)' }}>Page {page} of {lastPage}</span>
                        <button className="pagination-btn" disabled={page >= lastPage} onClick={() => { if (page < lastPage) fetchData(page + 1); }}>Next</button>
                    </div>
                </div>
            </div>

            {/* ── Filters Popup Modal ── */}
            {showFilters && (
                <div className="modal-overlay" onClick={() => setShowFilters(false)}>
                    <div className="modal-container filter-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '550px', width: '95%' }}>
                        <div className="modal-header">
                            <h2 style={{ fontSize: '1.1rem', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                                </svg>
                                Filter Purchase Payment Report
                            </h2>
                            <button className="modal-close-btn" onClick={() => setShowFilters(false)}>✕</button>
                        </div>
                        <div className="modal-content" style={{ padding: '1.5rem', maxWidth: 'none' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: '600', color: '#475569', marginBottom: '0.35rem' }}>Start Date:</label>
                                    <input type="date" value={pendingFilters.start_date} onChange={e => setPendingFilters(f => ({ ...f, start_date: e.target.value }))} style={{ width: '100%', padding: '0.45rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.875rem', outline: 'none' }} />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: '600', color: '#475569', marginBottom: '0.35rem' }}>End Date:</label>
                                    <input type="date" value={pendingFilters.end_date} onChange={e => setPendingFilters(f => ({ ...f, end_date: e.target.value }))} style={{ width: '100%', padding: '0.45rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.875rem', outline: 'none' }} />
                                </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: '600', color: '#475569', marginBottom: '0.35rem' }}>Location:</label>
                                    <select value={pendingFilters.location_id} onChange={e => setPendingFilters(f => ({ ...f, location_id: e.target.value }))} style={{ width: '100%', padding: '0.45rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.875rem', outline: 'none' }}>
                                        <option value="">All Locations</option>
                                        {filterOptions.locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: '600', color: '#475569', marginBottom: '0.35rem' }}>Supplier:</label>
                                    <select value={pendingFilters.supplier_id} onChange={e => setPendingFilters(f => ({ ...f, supplier_id: e.target.value }))} style={{ width: '100%', padding: '0.45rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.875rem', outline: 'none' }}>
                                        <option value="">All Suppliers</option>
                                        {filterOptions.suppliers.map(s => <option key={s.id} value={s.id}>{s.supplier_business_name || s.name}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div style={{ padding: '1rem 1.5rem', background: 'var(--etpos-card, #fff)', borderTop: '1px solid var(--etpos-border, #e2e8f0)', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.75rem' }}>
                            <button
                                type="button"
                                onClick={handleFilterReset}
                                style={{
                                    height: '38px',
                                    padding: '0 1.25rem',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '6px',
                                    background: '#f8fafc',
                                    color: '#475569',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.15s ease'
                                }}
                            >
                                Reset
                            </button>
                            <button
                                type="button"
                                onClick={handleFilterApply}
                                style={{
                                    height: '38px',
                                    padding: '0 1.25rem',
                                    border: 'none',
                                    borderRadius: '6px',
                                    background: '#3b82f6',
                                    color: '#ffffff',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 1px 2px rgba(59,130,246,0.2)',
                                    transition: 'all 0.15s ease'
                                }}
                            >
                                Apply Filters
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* View Payment Modal */}
            {showViewModal && selectedRowForView && (
                <div className="modal-overlay" onClick={() => setShowViewModal(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                    <div className="modal-container" onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '8px', maxWidth: '650px', width: '90%', overflow: 'hidden', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
                        {/* Header */}
                        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#334155', fontWeight: '600' }}>
                                View Payment ( Reference No: {selectedRowForView.payment_ref_no || '--'} )
                            </h3>
                            <button onClick={() => setShowViewModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#94a3b8' }}>✕</button>
                        </div>

                        {/* Body */}
                        <div style={{ padding: '1.5rem', fontSize: '0.875rem', color: '#334155' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '1.5rem' }}>
                                {/* Supplier Info */}
                                <div>
                                    <strong style={{ color: '#64748b', fontSize: '0.8rem', display: 'block', marginBottom: '0.25rem' }}>Supplier:</strong>
                                    <div style={{ fontWeight: '700', fontSize: '0.95rem', color: '#1e293b' }}>
                                        {selectedRowForView.supplier && selectedRowForView.supplier !== '' && selectedRowForView.supplier !== '--' 
                                            ? selectedRowForView.supplier 
                                            : (selectedRowForView.contact_name || selectedRowForView.supplier_name || 'N/A')}
                                    </div>
                                    {selectedRowForView.contact_address && <div style={{ color: '#475569', marginTop: '0.25rem', whiteSpace: 'pre-line' }}>{selectedRowForView.contact_address}</div>}
                                    {selectedRowForView.contact_mobile && <div style={{ color: '#475569', marginTop: '0.25rem' }}>Mobile: {selectedRowForView.contact_mobile}</div>}
                                </div>

                                {/* Business Info */}
                                <div>
                                    <strong style={{ color: '#64748b', fontSize: '0.8rem', display: 'block', marginBottom: '0.25rem' }}>Business:</strong>
                                    <div style={{ fontWeight: '700', fontSize: '0.95rem', color: '#1e293b' }}>
                                        {selectedRowForView.location_name || selectedRowForView.business_name || 'BALAJI ENTERPRISES 2022 LLC'}
                                    </div>
                                    <div style={{ color: '#475569', marginTop: '0.25rem', whiteSpace: 'pre-line' }}>4318 RIVERS AVENUE, North Charleston, SC, USA, 29405</div>
                                    <div style={{ color: '#475569', marginTop: '0.25rem' }}>Mobile: 843-783-3214</div>
                                    <div style={{ color: '#475569', marginTop: '0.1rem' }}>Email: info@balajienterprise2022llc.com</div>
                                </div>
                            </div>

                            <hr style={{ border: 'none', borderTop: '1px dashed #e2e8f0', margin: '1.25rem 0' }} />

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', background: '#f8fafc', padding: '1rem 1.25rem', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                                <div>
                                    <div style={{ marginBottom: '0.4rem' }}><strong>Amount :</strong> {selectedRowForView.amount_formatted || `$ ${selectedRowForView.amount}`}</div>
                                    <div style={{ marginBottom: '0.4rem' }}><strong>Payment Method :</strong> {selectedRowForView.method_label || selectedRowForView.method || '--'}</div>
                                    {selectedRowForView.cheque_number && <div style={{ marginBottom: '0.4rem' }}><strong>Cheque number :</strong> {selectedRowForView.cheque_number}</div>}
                                    {selectedRowForView.card_transaction_number && <div style={{ marginBottom: '0.4rem' }}><strong>Card Transaction No :</strong> {selectedRowForView.card_transaction_number}</div>}
                                    {selectedRowForView.bank_account_number && <div style={{ marginBottom: '0.4rem' }}><strong>Bank Account No :</strong> {selectedRowForView.bank_account_number}</div>}
                                    <div><strong>Payment Note :</strong> {selectedRowForView.note || '--'}</div>
                                </div>
                                <div>
                                    <div style={{ marginBottom: '0.4rem' }}><strong>Reference No:</strong> {selectedRowForView.payment_ref_no || '--'}</div>
                                    <div><strong>Paid on:</strong> {selectedRowForView.paid_on ? formatDateTime(selectedRowForView.paid_on) : '--'}</div>
                                </div>
                            </div>
                        </div>

                        {/* Footer Buttons */}
                        <div style={{ padding: '1rem 1.5rem', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                            <button
                                onClick={() => window.print()}
                                style={{ background: '#4f46e5', color: '#fff', border: 'none', padding: '0.5rem 1.25rem', borderRadius: '6px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
                            >
                                🖨️ Print
                            </button>
                            <button
                                onClick={() => setShowViewModal(false)}
                                style={{ background: '#334155', color: '#fff', border: 'none', padding: '0.5rem 1.25rem', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PurchasePaymentReport;
